import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import Footer from '../../components/Footer';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { primeFeedData, getMatchSessionId, setMatchSessionId, clearFeedPrefetch, recordLocalViewedCandidate, removeLocalViewedCandidates, getFeedRuntimeSnapshot, setFeedRuntimeSnapshot } from '../../utils/feedPrefetch';
import { useNavigate } from 'react-router-dom';
import { resolveAvatarThumbUrl, withAvatarFallback } from '../../utils/avatar';
import { API_BASE } from '../../config';
import useOnboardingGuard from '../../hooks/useOnboardingGuard';

const uniqueById = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const normalized = normalizeCandidate(item);
    const id = normalized?.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(normalized);
  }
  return out;
};

const normalizeCandidate = (item) => {
  if (!item) return item;
  return {
    ...item,
    id: item.id ?? item.Id,
    firstName: item.firstName ?? item.FirstName ?? '',
    lastName: item.lastName ?? item.LastName ?? '',
    nickname: item.nickname ?? item.Nickname ?? '',
    bio: item.bio ?? item.Bio,
    city: item.city ?? item.City,
    jobTitle: item.jobTitle ?? item.JobTitle,
    avatarUrl: item.avatarUrl ?? item.AvatarUrl,
    answers: item.answers ?? item.Answers ?? [],
    matchPercentage: item.matchPercentage ?? item.MatchPercentage,
    isLikedByMe: item.isLikedByMe ?? item.IsLikedByMe ?? false,
    isMutual: item.isMutual ?? item.IsMutual ?? false
  };
};

const getDisplayName = (item) => (
  [item?.firstName, item?.lastName].filter(Boolean).join(' ') || item?.nickname || 'Имя Фамилия'
);

const getCardMetaText = (item) => (
  `${item?.city || 'Город'}${item?.age ? `, ${item.age} лет` : ''}, ${item?.jobTitle || 'Профессия'}`
);

const mergeUniqueById = (left, right, blockedIds = new Set()) => {
  const out = [];
  const seen = new Set();

  const add = (item) => {
    const id = item?.id;
    if (!id || seen.has(id) || blockedIds.has(id)) return;
    seen.add(id);
    out.push(item);
  };

  (left || []).forEach(add);
  (right || []).forEach(add);
  return out;
};


export default function Feed(){
  const navigate = useNavigate();
  const { loading: onboardingLoading } = useOnboardingGuard();
  const [feed, setFeed] = useState([]); // fresh cards from server
  const [history, setHistory] = useState([]); // previously viewed (oldest first)
  const [historyOffset, setHistoryOffset] = useState(0);
  const FEED_FETCH = 18;
  const FEED_LOW_WATER = 5;
  const MAX_TAGS = 16;
  const MAX_TAG_LENGTH = 24;
  const MAX_TAG_WORD_LENGTH = 18;
  const HISTORY_FETCH = 10;
  const CARD_SHIFT_MS = 420;
  const DESKTOP_NEXT_PHASE1_MS = 280;
  const DESKTOP_NEXT_PHASE2_MS = 360;
  const MOBILE_CARD_SLIDE_MS = 650;
  const MOBILE_FEED_CARD_METRICS = {
    tagTopSpace: 4,
    tagBottomSpace: 4,
    tagRowGap: 2.7,
    tagColumnGap: 3.2,
    maxTagRows: 4,
    tagAvailableWidth: 212.68,
    tagPaddingX: 6.3,
    tagFontSize: 10.1,
    tagLineHeightMultiplier: 1.4,
    tagPaddingY: 6.33,
    tagBorderWidth: 0.63,
    tagMinHeight: 28.2,
    bioLineHeight: 11.4,
    availableHeightForTagsAndBio: 178
  };
  const MAX_DESCRIPTION_LINES = 6;
  const DESKTOP_FEED_CARD_METRICS = {
    cardHeight: 683.36,
    cardPadTop: 2.35,
    cardPadBottom: 23.46,
    photoHeight: 226.4,
    bodyTop: 17.6,
    titleFontSize: 28.15,
    titleLineHeight: 28.15,
    titleGap: 3.5,
    titleAvailableWidth: 390.17,
    metaFontSize: 23.46,
    metaLineHeight: 23.46,
    metaAvailableWidth: 390.17,
    tagTopSpace: 0,
    tagBottomSpace: 0,
    tagRowGap: 5,
    tagColumnGap: 5.87,
    tagInnerGap: 4.69,
    tagFontSize: 18.77,
    tagLineHeightMultiplier: 1.4,
    tagPaddingX: 11.73,
    tagPaddingY: 11.73,
    tagBorderWidth: 1.17,
    tagBorderRadius: 11.73,
    tagLetterSpacing: 0.23,
    tagAvailableWidth: 385.49,
    bioFontSize: 23.46,
    bioAvailableWidth: 390.17,
    bioLineHeight: 21.11,
    descriptionActionsGap: 17.59,
    actionsHeight: 41.06
  };
  const getMobileTagRowHeight = () => {
    const contentHeight =
      (MOBILE_FEED_CARD_METRICS.tagFontSize * MOBILE_FEED_CARD_METRICS.tagLineHeightMultiplier) +
      (MOBILE_FEED_CARD_METRICS.tagPaddingY * 2) +
      (MOBILE_FEED_CARD_METRICS.tagBorderWidth * 2);

    return Math.max(MOBILE_FEED_CARD_METRICS.tagMinHeight, contentHeight);
  };
  const getDesktopTagRowHeight = () => (
    (DESKTOP_FEED_CARD_METRICS.tagFontSize * DESKTOP_FEED_CARD_METRICS.tagLineHeightMultiplier) +
    (DESKTOP_FEED_CARD_METRICS.tagPaddingY * 2) +
    (DESKTOP_FEED_CARD_METRICS.tagBorderWidth * 2)
  );
  const getMobileScaleFactor = () => {
    if (typeof window === 'undefined') return 1;
    return window.innerWidth / 360;
  };
  const getDesktopScaleFactor = () => {
    if (typeof window === 'undefined') return 1;
    return Math.min(window.innerWidth, 2560) / 1920;
  };
  const scaleMobileMetric = (value) => value * getMobileScaleFactor();
  const scaleDesktopMetric = (value) => value * getDesktopScaleFactor();
  const getTagBlockHeight = (rows, rowHeight, rowGap, topSpace, bottomSpace) => {
    if (rows <= 0) return 0;
    return topSpace + bottomSpace + (rowHeight * rows) + (rowGap * Math.max(0, rows - 1));
  };
  const parsePixels = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const toMobileCssPixels = (value) => (
    getMobileScaleFactor() < 1
      ? `${Math.ceil(value)}px`
      : `${Math.floor(value)}px`
  );
  const toStrictCssPixels = (value) => `${Math.floor(value)}px`;
  const toSafeCssPixels = (value, extra = 1) => `${Math.ceil(value + extra)}px`;
  const getDesktopTagBlockHeight = (rows, rowHeight, rowGap, topSpace, bottomSpace) => (
    rows <= 0
      ? 0
      : getTagBlockHeight(rows, rowHeight, rowGap, topSpace, bottomSpace)
  );
  const [viewedSet, setViewedSet] = useState(() => new Set());
  const [likedSet, setLikedSet] = useState(() => new Set(JSON.parse(localStorage.getItem('liked_candidates') || '[]')));
  const [isFetching, setIsFetching] = useState(false);
  const [likeAnimations, setLikeAnimations] = useState({});
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cardTransition, setCardTransition] = useState(null);
  const [desktopSlots, setDesktopSlots] = useState(null);
  const [desktopSlotTransition, setDesktopSlotTransition] = useState(null);
  const prevCardRef = useRef(null);
  const mainCardRef = useRef(null);
  const nextCardRef = useRef(null);
  const nameHydrationRef = useRef(new Set());
  const textMeasureCanvasRef = useRef(null);
  const transitionRunRef = useRef(0);
  const recycleInFlightRef = useRef(false);
  const viewingInFlightRef = useRef(new Set());
  const defaultCardLayout = {
    '--feed-visible-tag-rows': 2,
    '--feed-visible-tag-count': MAX_TAGS,
    '--feed-description-lines': 4,
    '--feed-tags-center-top': '0px',
    '--feed-tags-center-bottom': '0px',
    '--feed-tags-block-height': 'auto'
  };
  const [cardLayouts, setCardLayouts] = useState({});

  const markCardViewed = (card) => {
    const id = card?.id;
    if (!id || viewedSet.has(id) || viewingInFlightRef.current.has(id)) return;

    viewingInFlightRef.current.add(id);
    recordLocalViewedCandidate(card);
    setViewedSet((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    const token = localStorage.getItem('token');
    if (!token) {
      viewingInFlightRef.current.delete(id);
      return;
    }

    (async () => {
      try {
        const sessionId = getMatchSessionId();
        const viewedUrl = sessionId
          ? `${API_BASE}/api/Matching/viewed?sessionId=${encodeURIComponent(sessionId)}`
          : `${API_BASE}/api/Matching/viewed`;
        await fetch(viewedUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: id })
        });
      } catch (e) {
        console.warn('mark viewed failed', e);
      } finally {
        viewingInFlightRef.current.delete(id);
      }
    })();
  };
  
  const applyFeedData = (data, options = {}) => {
    if (!data) return;

    const nextFeed = Array.isArray(data.feed) ? uniqueById(data.feed) : [];
    const nextHistory = Array.isArray(data.history) ? uniqueById(data.history) : [];
    const historyIdSet = new Set(nextHistory.map((x) => x.id));
    const filteredFeed = nextFeed.filter((x) => !historyIdSet.has(x.id));

    setFeed(filteredFeed);
    setHistory(nextHistory);

    try{
      const localLiked = new Set(JSON.parse(localStorage.getItem('liked_candidates') || '[]'));
      const serverLiked = new Set();
      filteredFeed.forEach(i => { if (i && i.isLikedByMe) serverLiked.add(i.id); });
      nextHistory.forEach(i => { if (i && i.isLikedByMe) serverLiked.add(i.id); });
      const merged = new Set([...localLiked, ...serverLiked]);
      setLikedSet(merged);
      localStorage.setItem('liked_candidates', JSON.stringify(Array.from(merged)));
    }catch(e){ /* ignore localStorage parsing errors */ }

    setHistoryOffset(data.historyOffset ?? nextHistory.length);
    const totalItems = nextHistory.length + filteredFeed.length;
    const requestedIndex = Number.isInteger(options.index) ? options.index : null;
    const defaultIndex = nextHistory.length > 0 && filteredFeed.length > 0 ? nextHistory.length : 0;
    const nextIndex = requestedIndex === null
      ? defaultIndex
      : Math.max(0, Math.min(requestedIndex, Math.max(0, totalItems - 1)));
    setIndex(nextIndex);
    setFinished(Boolean(options.finished) && totalItems === 0);

    const initialItems = nextHistory.concat(filteredFeed);
    const initialCard = initialItems[nextIndex];
    const isFreshInitialCard = Boolean(initialCard) && nextIndex >= nextHistory.length;
    if (isFreshInitialCard) {
      markCardViewed(initialCard);
    }
  };

  const loadFeedState = async () => {
    const token = localStorage.getItem('token');
    if (!token){
      navigate('/log_in');
      return;
    }

    const runtimeSnapshot = getFeedRuntimeSnapshot();
    if (runtimeSnapshot?.data) {
      applyFeedData(runtimeSnapshot.data, {
        index: runtimeSnapshot.index,
        finished: runtimeSnapshot.finished
      });
      return;
    }

    const data = await primeFeedData();
    if (!data) return;
    applyFeedData(data);
  };

  const recycleViewedAndReload = async () => {
    if (recycleInFlightRef.current) return false;

    const token = localStorage.getItem('token');
    if (!token) return false;

    recycleInFlightRef.current = true;
    try {
      const resp = await fetch(`${API_BASE}/api/Matching/recycle-viewed?ratio=0.7`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!resp.ok) return false;

      const payload = await resp.json();
      const removedIds = Array.isArray(payload?.removedIds) ? payload.removedIds.filter(Boolean) : [];
      if (removedIds.length === 0) return false;

      setViewedSet((prev) => {
        const next = new Set(prev);
        removedIds.forEach((id) => next.delete(id));
        return next;
      });
      removeLocalViewedCandidates(removedIds);

      clearFeedPrefetch();
      await loadFeedState();
      return true;
    } catch (e) {
      console.warn('recycle viewed failed', e);
      return false;
    } finally {
      recycleInFlightRef.current = false;
    }
  };

  // Touch state for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
       next();
    } else if (isRightSwipe) {
       prev();
    }
  };

  useEffect(()=>{
    let active = true;
    const load = async ()=>{
      try{
        if (!active) return;
        await loadFeedState();
      }catch(e){
        console.error('Feed load error', e);
      }finally{
        if (active) setLoading(false);
      }
    };
    load();
    return ()=>{ active = false; };
  },[]);

  // Reset finished state if we receive more data while showing the "finished" screen
  useEffect(() => {
    if (finished && index < history.length + feed.length) {
      setFinished(false);
    }
  }, [feed, history, index, finished]);

  useEffect(() => {
    if (loading) return;
    setFeedRuntimeSnapshot({
      data: {
        feed,
        history,
        historyOffset
      },
      index,
      finished
    });
  }, [loading, feed, history, historyOffset, index, finished]);

  // send 'viewed' for current card (only once) - placed before early returns to satisfy hooks rules
  useEffect(() => {
    if (isAnimating) return;
    const itemsToShow = history.concat(feed); 
    if (itemsToShow.length === 0) return;

    const idx = Math.max(0, Math.min(index, itemsToShow.length - 1));
    const isHistoryCard = idx < history.length;
    if (isHistoryCard) return;
    const card = itemsToShow[idx];
    if (!card) return;
    markCardViewed(card);
  }, [index, feed, history, viewedSet, isAnimating]);

  useEffect(() => {
    if (loading) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const itemsToShow = history.concat(feed);
    if (itemsToShow.length === 0) return;

    const idx = Math.max(0, Math.min(index, itemsToShow.length - 1));
    const visibleCards = [
      itemsToShow[idx === 0 ? itemsToShow.length - 1 : idx - 1],
      itemsToShow[idx],
      itemsToShow[idx === itemsToShow.length - 1 ? 0 : idx + 1]
    ];

    const missingNames = [];
    const seen = new Set();
    visibleCards.forEach((item) => {
      const id = item?.id;
      if (!id || item.lastName || nameHydrationRef.current.has(id) || seen.has(id)) return;
      seen.add(id);
      missingNames.push(item);
    });
    if (missingNames.length === 0) return;

    const mergeProfileName = (profile) => {
      const normalized = normalizeCandidate(profile);
      if (!normalized?.id) return;
      const merge = (item) => item?.id === normalized.id
        ? {
            ...item,
            firstName: normalized.firstName || item.firstName,
            lastName: normalized.lastName || item.lastName,
            nickname: normalized.nickname || item.nickname
          }
        : item;
      setFeed((prev) => prev.map(merge));
      setHistory((prev) => prev.map(merge));
    };

    missingNames.forEach((item) => {
      const id = item.id;
      nameHydrationRef.current.add(id);
      (async () => {
        try {
          const resp = await fetch(`${API_BASE}/api/Profile/by-id/${encodeURIComponent(id)}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!resp.ok) return;
          mergeProfileName(await resp.json());
        } catch (e) {
          nameHydrationRef.current.delete(id);
          console.warn('profile name hydration failed', e);
        }
      })();
    });
  }, [loading, feed, history, index]);

  const getLength = ()=> {
    return history.length + feed.length;
  };

  const finishCardTransition = (nextIndex, nextDesktopSlots = null) => {
    setIndex(nextIndex);
    if (nextDesktopSlots) setDesktopSlots(nextDesktopSlots);
    setIsAnimating(false);
    setCardTransition(null);
    setDesktopSlotTransition(null);
  };

  const createCardTransition = (direction, fromIndex, toIndex, phase = null) => {
    transitionRunRef.current += 1;
    return { direction, fromIndex, toIndex, phase, runId: transitionRunRef.current };
  };

  const next = ()=>{
    if (isAnimating) return;
    const isDesktopViewport = window.innerWidth >= 768;
    const itemsToShow = history.concat(feed);

    if (itemsToShow.length === 0) return;
    
    let nextIndex = index + 1;
    // Loop back to the beginning if we reached the end
    if (nextIndex >= getLength()){
      setFinished(true);
      void recycleViewedAndReload();
      return;
    }

    if (isDesktopViewport) {
      const fromSlots = desktopSlots || buildDesktopSlots(index, itemsToShow, cardLayouts);
      const toSlots = shiftDesktopSlotsNext(fromSlots, nextIndex, itemsToShow, cardLayouts);
      const transition = createCardTransition('next', index, nextIndex, 'stack-left');
      setDesktopSlotTransition({ from: fromSlots, to: toSlots });
      setCardTransition(transition);
      setIsAnimating(true);
      setTimeout(() => {
        setCardTransition((current) => (
          current?.runId === transition.runId
            ? { ...current, phase: 'promote-right' }
            : current
        ));
      }, DESKTOP_NEXT_PHASE1_MS);
      setTimeout(() => finishCardTransition(nextIndex, toSlots), DESKTOP_NEXT_PHASE1_MS + DESKTOP_NEXT_PHASE2_MS);
    } else {
      setCardTransition(createCardTransition('next', index, nextIndex));
      setIsAnimating(true);
      setTimeout(() => finishCardTransition(nextIndex), MOBILE_CARD_SLIDE_MS);
    }


    // if we are running low on feed cards, fetch more
    // Note: if we looped to 0, feedPos might be negative or small, which is fine
    const feedPos = nextIndex - history.length + 1; 
    const remainingFeed = feed.length - feedPos;
    
    // Only fetch if we are actually in the feed section (not history) and running low
    if (nextIndex >= history.length && remainingFeed <= FEED_LOW_WATER) {
      const token = localStorage.getItem('token');
      if (token) {
        setIsFetching(true);
        (async () => {
          try{
            const headers = { 'Authorization': `Bearer ${token}` };
            const fetchBatch = async (sessionId) => {
              const params = new URLSearchParams({ count: String(FEED_FETCH) });
              if (sessionId) params.set('sessionId', sessionId);
              const resp = await fetch(`${API_BASE}/api/Matching/batch?${params.toString()}`, { headers });
              const respSessionId = resp.headers.get('X-Match-Session');
              if (respSessionId) setMatchSessionId(respSessionId);
              if (!resp.ok) return { ok: false, items: [] };
              const payload = await resp.json();
              return { ok: true, items: Array.isArray(payload) ? payload : [] };
            };

            const currentSessionId = getMatchSessionId();
            let result = await fetchBatch(currentSessionId);

            // Session may be exhausted; reset and retry once.
            if (result.ok && result.items.length === 0 && currentSessionId) {
              setMatchSessionId(null);
              result = await fetchBatch(null);
            }

            if (result.ok){
              const hidden = JSON.parse(localStorage.getItem('hidden_candidates') || '[]');
              const filtered = uniqueById(result.items.filter(d => !hidden.includes(d.id)));
              setFeed(f => {
                try{
                  const serverLiked = new Set();
                  filtered.forEach(i => { if (i && i.isLikedByMe) serverLiked.add(i.id); });
                  if (serverLiked.size > 0){
                    setLikedSet(prev => {
                      const merged = new Set(prev);
                      serverLiked.forEach(id => merged.add(id));
                      localStorage.setItem('liked_candidates', JSON.stringify(Array.from(merged)));
                      return merged;
                    });
                  }
                }catch(e){}
                const historyIdSet = new Set(history.map((x) => x.id));
                return mergeUniqueById(f, filtered, historyIdSet);
              });
            }
          }catch(e){ console.warn('fetch more batch failed', e); }
          finally { setIsFetching(false); }
        })();
      }
    }
  };

  const prev = ()=>{
    if (isAnimating) return;
    const isDesktopViewport = window.innerWidth >= 768;
    const itemsToShow = history.concat(feed);
    
    let prevIndex = index - 1;
    // Loop to the end if we reached the start
    if (prevIndex < 0){
        prevIndex = Math.max(0, getLength() - 1);
    }

    if (isDesktopViewport) {
      const fromSlots = desktopSlots || buildDesktopSlots(index, itemsToShow, cardLayouts);
      const toSlots = shiftDesktopSlotsPrev(fromSlots, prevIndex, itemsToShow, cardLayouts);
      const transition = createCardTransition('prev', index, prevIndex, 'stack-right');
      setDesktopSlotTransition({ from: fromSlots, to: toSlots });
      setCardTransition(transition);
      setIsAnimating(true);
      setTimeout(() => {
        setCardTransition((current) => (
          current?.runId === transition.runId
            ? { ...current, phase: 'promote-left' }
            : current
        ));
      }, DESKTOP_NEXT_PHASE1_MS);
      setTimeout(() => finishCardTransition(prevIndex, toSlots), DESKTOP_NEXT_PHASE1_MS + DESKTOP_NEXT_PHASE2_MS);
    } else {
      setCardTransition(createCardTransition('prev', index, prevIndex, null));
      setIsAnimating(true);
      setTimeout(() => finishCardTransition(prevIndex), MOBILE_CARD_SLIDE_MS);
    }


    // if we are showing the earliest known history and there may be more, fetch older history.
    // NOTE: fetching history changes indices, which might be tricky during loop. 
    // We only fetch if we are logically at the start of history (index 0 before loop).
    if (index === 0) {
      const token = localStorage.getItem('token');
      if (token) {
        setIsFetching(true);
        (async () => {
          try{
            const resp = await fetch(`${API_BASE}/api/Matching/history?limit=${HISTORY_FETCH}&offset=${historyOffset}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (resp.ok){
              const data = await resp.json();
              const hItems = Array.isArray(data.items) ? data.items : [];
              if (hItems.length > 0){
                const ordered = uniqueById(hItems.slice().reverse());
                setHistory(h => mergeUniqueById(ordered, h));
                setHistoryOffset(data.nextOffset ?? (historyOffset + hItems.length));
                // shift index so user stays on same card (which is now pushed forward)
                // However, we just moved to prevIndex (end of list?). 
                // If we looped, we are at end. If we didn't loop (impossible if index=0), we are at new end?
                // Actually if we just looped from 0 -> end, index is `feed.length + history.length - 1`.
                // Adding to history increases `history.length`.
                // So the "end" index increases.
                setIndex(prev => prev + hItems.length);
              }
            }
          }catch(e){ console.warn('fetch more history failed', e); }
          finally { setIsFetching(false); }
        })();
      }
    }
  };

  const markNotInterested = async (id)=>{
    if (isAnimating) return;
    
    // add to local hidden list
    const hidden = JSON.parse(localStorage.getItem('hidden_candidates') || '[]');
    if (!hidden.includes(id)){
      hidden.push(id);
      localStorage.setItem('hidden_candidates', JSON.stringify(hidden));
    }

    // Instead of removing the card, we just advance to the next one so we can go back later
    next();

    // notify server (so backend may exclude in future queries)
    const token = localStorage.getItem('token');
    if (token){
      try{
        const sessionId = getMatchSessionId();
        const viewedUrl = sessionId
          ? `${API_BASE}/api/Matching/viewed?sessionId=${encodeURIComponent(sessionId)}`
          : `${API_BASE}/api/Matching/viewed`;
        await fetch(viewedUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: id })
        });
      }catch(e){ console.warn('notify viewed failed', e); }
    }
  };

  const toggleLike = async (id)=>{
    if (isAnimating) return;
    
    const token = localStorage.getItem('token');
    if (!token) { window.location.href = '/log_in'; return; }

    const currentItem = feed.find((item) => item.id === id) || history.find((item) => item.id === id);
    const currentLiked = likedSet.has(id) || Boolean(currentItem?.isLikedByMe);
    const nextLiked = !currentLiked;

    // optimistic UI: toggle locally so heart fills right away and update likedSet
    let prevFeed;
    let prevHistory;
    let prevLiked;
    setFeed(f => {
      prevFeed = f;
      return f.map(x => x.id === id ? {...x, isLikedByMe: !x.isLikedByMe} : x);
    });
    setHistory(h => {
      prevHistory = h;
      return h.map(x => x.id === id ? {...x, isLikedByMe: !x.isLikedByMe} : x);
    });
    // optimistic likedSet update
    setLikedSet(prev => {
      prevLiked = new Set(prev);
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      try{ localStorage.setItem('liked_candidates', JSON.stringify(Array.from(s))); }catch(e){}
      return s;
    });
    setLikeAnimations((prev) => ({
      ...prev,
      [id]: nextLiked ? 'like' : 'unlike'
    }));

    try{
      const resp = await fetch(`${API_BASE}/api/Likes/like/${id}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) {
        console.warn('like failed, reverting');
        // revert optimistic change
        if (prevFeed) setFeed(() => prevFeed);
        if (prevHistory) setHistory(() => prevHistory);
        if (prevLiked) {
          setLikedSet(() => prevLiked);
          try{ localStorage.setItem('liked_candidates', JSON.stringify(Array.from(prevLiked))); }catch(e){}
        }
        return;
      }
      const body = await resp.json();
      const liked = body.liked === undefined ? true : !!body.liked;
      // ensure server-confirmed state and persist likedSet
      setFeed(f => f.map(x => x.id === id ? {...x, isLikedByMe: liked} : x));
      setHistory(h => h.map(x => x.id === id ? {...x, isLikedByMe: liked} : x));
      setLikedSet(prev => {
        const s = new Set(prev);
        if (liked) s.add(id); else s.delete(id);
        try{ localStorage.setItem('liked_candidates', JSON.stringify(Array.from(s))); }catch(e){}
        return s;
      });
    }catch(e){
      console.error(e);
      // revert on error
      if (prevFeed) setFeed(() => prevFeed);
      if (prevHistory) setHistory(() => prevHistory);
    }
  };


  const getTags = (item) => {
    if (!item || !item.answers || item.answers.length === 0) return [];
    const seen = new Set();
    return item.answers
      .flatMap(q => q.selected || [])
      .map(tag => {
        if (typeof tag === 'string') return tag;
        return tag?.text || tag?.name || tag?.Name || '';
      })
      .map(tag => String(tag).trim())
      .filter(tag => {
        const hasTooLongWord = tag.split(/\s+/).some(word => word.length > MAX_TAG_WORD_LENGTH);
        if (tag.length > MAX_TAG_LENGTH || hasTooLongWord) return false;
        if (!tag || seen.has(tag)) return false;
        if (tag.toLowerCase() === 'другое' || tag.toLowerCase().startsWith('option #')) return false;
        seen.add(tag);
        return true;
      })
      .slice(0, MAX_TAGS);
  };

  const estimateVisibleTagCount = (tagsList, {
    maxRows,
    fontSize,
    fontWeight,
    letterSpacing,
    paddingX,
    borderWidth,
    columnGap,
    maxWidth
  }) => {
    if (!Array.isArray(tagsList) || tagsList.length === 0 || maxRows <= 0) return 0;
    const ctx = getTextMeasureContext();
    if (!ctx || !Number.isFinite(maxWidth) || maxWidth <= 0) {
      return Math.min(tagsList.length, maxRows * 2);
    }

    ctx.font = `${fontWeight} ${fontSize}px "TT Norms Pro", sans-serif`;
    const measure = (value) => {
      if (!value) return 0;
      return ctx.measureText(value).width + (letterSpacing * Math.max(0, value.length - 1));
    };

    const pillBaseWidth = (paddingX * 2) + (borderWidth * 2);
    let rows = 1;
    let currentRowWidth = 0;
    let visibleCount = 0;

    for (const tag of tagsList) {
      const pillWidth = Math.min(maxWidth, pillBaseWidth + measure(tag));
      const nextWidth = currentRowWidth === 0
        ? pillWidth
        : currentRowWidth + columnGap + pillWidth;

      if (nextWidth <= maxWidth) {
        currentRowWidth = nextWidth;
        visibleCount += 1;
        continue;
      }

      rows += 1;
      if (rows > maxRows) break;
      currentRowWidth = pillWidth;
      visibleCount += 1;
    }

    return visibleCount;
  };

  const getVisibleTagCountFromDom = (tagsRoot, maxRows) => {
    if (!tagsRoot || maxRows <= 0) return 0;
    const pills = Array.from(tagsRoot.children || []);
    if (pills.length === 0) return 0;

    let rowCount = 0;
    let visibleCount = 0;
    let lastTop = null;

    for (const pill of pills) {
      const top = pill.offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 1) {
        rowCount += 1;
        lastTop = top;
      }
      if (rowCount > maxRows) break;
      visibleCount += 1;
    }

    return visibleCount;
  };

  const getVisibleTagMetricsFromDom = (tagsRoot, maxRows) => {
    if (!tagsRoot || maxRows <= 0) {
      return { visibleCount: 0, blockHeight: 0 };
    }

    const pills = Array.from(tagsRoot.children || []);
    if (pills.length === 0) {
      return { visibleCount: 0, blockHeight: 0 };
    }

    const styles = window.getComputedStyle(tagsRoot);
    const paddingBottom = parsePixels(styles.paddingBottom);
    let rowCount = 0;
    let visibleCount = 0;
    let lastTop = null;
    let maxBottom = 0;

    for (const pill of pills) {
      const top = pill.offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 1) {
        rowCount += 1;
        lastTop = top;
      }
      if (rowCount > maxRows) break;
      visibleCount += 1;
      maxBottom = Math.max(maxBottom, pill.offsetTop + pill.offsetHeight);
    }

    return {
      visibleCount,
      blockHeight: maxBottom > 0 ? Math.ceil(maxBottom + paddingBottom + 1) : 0
    };
  };

  const getStaticCardLayout = (card) => {
    if (!card) return defaultCardLayout;
    
    const isMobileView = window.innerWidth < 768;
    const tagsList = getTags(card) || [];
    const bioText = card.bio || '';
    
    if (isMobileView) {
      const mobileScale = getMobileScaleFactor();
      const estimatedTagRows = tagsList.length > 0 ? MOBILE_FEED_CARD_METRICS.maxTagRows : 0;
      const estimatedBioLines = Math.max(1, Math.ceil(bioText.length / 30));
      const targetBioLines = Math.min(MAX_DESCRIPTION_LINES, estimatedBioLines);
      const tagTopSpace = scaleMobileMetric(MOBILE_FEED_CARD_METRICS.tagTopSpace);
      const tagBottomSpace = scaleMobileMetric(MOBILE_FEED_CARD_METRICS.tagBottomSpace);
      const tagRowGap = scaleMobileMetric(MOBILE_FEED_CARD_METRICS.tagRowGap);
      const tagRowHeight = getMobileTagRowHeight() * mobileScale;
      const bioLineHeight = MOBILE_FEED_CARD_METRICS.bioLineHeight * mobileScale;
      
      let nextTagRows = 0;
      let nextDescriptionLines = targetBioLines;
      
      const bioHeight = targetBioLines * bioLineHeight;
      const availableHeightForTags = scaleMobileMetric(MOBILE_FEED_CARD_METRICS.availableHeightForTagsAndBio) - bioHeight;
      
      nextTagRows = estimatedTagRows > 0 ? 2 : 0;
      
      for (let rows = nextTagRows + 1; rows <= estimatedTagRows; rows += 1) {
        if (getTagBlockHeight(rows, tagRowHeight, tagRowGap, tagTopSpace, tagBottomSpace) <= availableHeightForTags) {
          nextTagRows = rows;
        } else {
          break;
        }
      }
      
      const isTruncated = estimatedBioLines > nextDescriptionLines ? 1 : 0;
      const nextVisibleTagCount = estimateVisibleTagCount(tagsList, {
        maxRows: nextTagRows,
        fontSize: MOBILE_FEED_CARD_METRICS.tagFontSize * mobileScale,
        fontWeight: 700,
        letterSpacing: 0,
        paddingX: MOBILE_FEED_CARD_METRICS.tagPaddingX * mobileScale,
        borderWidth: MOBILE_FEED_CARD_METRICS.tagBorderWidth * mobileScale,
        columnGap: MOBILE_FEED_CARD_METRICS.tagColumnGap * mobileScale,
        maxWidth: MOBILE_FEED_CARD_METRICS.tagAvailableWidth * mobileScale
      });
      
      return {
        '--feed-visible-tag-rows': nextTagRows,
        '--feed-visible-tag-count': nextVisibleTagCount,
        '--feed-description-lines': nextDescriptionLines,
        '--feed-tags-center-top': `${tagTopSpace}px`,
        '--feed-tags-center-bottom': `${tagBottomSpace}px`,
        '--feed-tags-block-height': toMobileCssPixels(getTagBlockHeight(nextTagRows, tagRowHeight, tagRowGap, tagTopSpace, tagBottomSpace)),
        '--feed-description-truncated': isTruncated
      };
    } else {
      const desktopScale = getDesktopScaleFactor();
      const estimatedTagRows = estimateDesktopTagRows(tagsList);
      const estimatedTitleLines = estimateWrappedTextLines(getDisplayName(card), {
        fontSize: DESKTOP_FEED_CARD_METRICS.titleFontSize * desktopScale,
        fontWeight: 900,
        letterSpacing: 0,
        maxWidth: DESKTOP_FEED_CARD_METRICS.titleAvailableWidth * desktopScale
      });
      const estimatedMetaLines = estimateWrappedTextLines(getCardMetaText(card), {
        fontSize: DESKTOP_FEED_CARD_METRICS.metaFontSize * desktopScale,
        fontWeight: 400,
        letterSpacing: 0,
        maxWidth: DESKTOP_FEED_CARD_METRICS.metaAvailableWidth * desktopScale
      });
      const estimatedBioLines = estimateWrappedTextLines(bioText || '...', {
        fontSize: DESKTOP_FEED_CARD_METRICS.bioFontSize * desktopScale,
        fontWeight: 400,
        letterSpacing: 0,
        maxWidth: DESKTOP_FEED_CARD_METRICS.bioAvailableWidth * desktopScale
      });
      const targetBioLines = Math.min(MAX_DESCRIPTION_LINES, estimatedBioLines);
      const tagTopSpace = scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.tagTopSpace);
      const tagBottomSpace = scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.tagBottomSpace);
      const tagRowGap = scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.tagRowGap);
      const tagRowHeight = getDesktopTagRowHeight() * desktopScale;
      const bioLineHeight = DESKTOP_FEED_CARD_METRICS.bioLineHeight * desktopScale;
      const titleHeight = estimatedTitleLines * (DESKTOP_FEED_CARD_METRICS.titleLineHeight * desktopScale);
      const metaHeight = estimatedMetaLines * (DESKTOP_FEED_CARD_METRICS.metaLineHeight * desktopScale);
      const bioHeight = targetBioLines * bioLineHeight;
      const availableHeightForTags = Math.max(
        getDesktopTagBlockHeight(estimatedTagRows > 0 ? 2 : 0, tagRowHeight, tagRowGap, tagTopSpace, tagBottomSpace),
        scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.cardHeight) -
          scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.cardPadTop) -
          scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.photoHeight) -
          scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.bodyTop) -
          titleHeight -
          scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.titleGap) -
          metaHeight -
          bioHeight -
          scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.descriptionActionsGap) -
          scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.actionsHeight) -
          scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.cardPadBottom)
      );
      let nextTagRows = estimatedTagRows > 0 ? 2 : 0;
      
      for (let rows = nextTagRows + 1; rows <= estimatedTagRows; rows += 1) {
        if (getDesktopTagBlockHeight(rows, tagRowHeight, tagRowGap, tagTopSpace, tagBottomSpace) <= availableHeightForTags) {
          nextTagRows = rows;
        } else {
          break;
        }
      }
      const nextVisibleTagCount = estimateVisibleTagCount(tagsList, {
        maxRows: nextTagRows,
        fontSize: DESKTOP_FEED_CARD_METRICS.tagFontSize * desktopScale,
        fontWeight: 700,
        letterSpacing: DESKTOP_FEED_CARD_METRICS.tagLetterSpacing * desktopScale,
        paddingX: DESKTOP_FEED_CARD_METRICS.tagPaddingX * desktopScale,
        borderWidth: DESKTOP_FEED_CARD_METRICS.tagBorderWidth * desktopScale,
        columnGap: DESKTOP_FEED_CARD_METRICS.tagColumnGap * desktopScale,
        maxWidth: DESKTOP_FEED_CARD_METRICS.tagAvailableWidth * desktopScale
      });

      return {
        '--feed-visible-tag-rows': nextTagRows,
        '--feed-visible-tag-count': nextVisibleTagCount,
        '--feed-description-lines': targetBioLines,
        '--feed-tags-center-top': `${tagTopSpace}px`,
        '--feed-tags-center-bottom': `${tagBottomSpace}px`,
        '--feed-tags-block-height': toSafeCssPixels(getDesktopTagBlockHeight(nextTagRows, tagRowHeight, tagRowGap, tagTopSpace, tagBottomSpace)),
        '--feed-description-truncated': estimatedBioLines > targetBioLines ? 1 : 0
      };
    }
  };

  const cloneLayout = (layout) => (
    layout
      ? { ...layout }
      : { ...defaultCardLayout }
  );

  const createFrozenSlotCard = (card, layoutMap, preferMeasuredLayout = false) => {
    if (!card) return null;
    return {
      card,
      layout: cloneLayout(
        preferMeasuredLayout
          ? (layoutMap[card.id] || getStaticCardLayout(card))
          : getStaticCardLayout(card)
      )
    };
  };

  const cloneSlotCard = (slotCard) => {
    if (!slotCard?.card) return null;
    return {
      card: slotCard.card,
      layout: cloneLayout(slotCard.layout)
    };
  };

  const buildDesktopSlots = (centerIndex, sourceItems, layoutMap) => {
    const at = (slotIndex, preferMeasuredLayout = false) => (
      slotIndex >= 0 && slotIndex < sourceItems.length
        ? createFrozenSlotCard(sourceItems[slotIndex], layoutMap, preferMeasuredLayout)
        : null
    );

    return {
      farLeft: at(centerIndex - 2, false),
      left: at(centerIndex - 1, false),
      center: at(centerIndex, false),
      right: at(centerIndex + 1, false),
      farRight: at(centerIndex + 2, false)
    };
  };

  const getTextMeasureContext = () => {
    if (typeof document === 'undefined') return null;
    if (!textMeasureCanvasRef.current) {
      textMeasureCanvasRef.current = document.createElement('canvas');
    }
    return textMeasureCanvasRef.current.getContext('2d');
  };

  const estimateWrappedTextLines = (text, {
    fontSize,
    fontWeight = 400,
    letterSpacing = 0,
    maxWidth
  }) => {
    const content = String(text || '...');
    const ctx = getTextMeasureContext();
    if (!ctx || !Number.isFinite(maxWidth) || maxWidth <= 0) {
      return Math.max(1, Math.ceil(content.length / 42));
    }

    ctx.font = `${fontWeight} ${fontSize}px "TT Norms Pro", sans-serif`;
    const measure = (value) => {
      if (!value) return 0;
      return ctx.measureText(value).width + (letterSpacing * Math.max(0, value.length - 1));
    };

    const breakLongToken = (token) => {
      if (!token) return 1;
      let lines = 1;
      let currentWidth = 0;
      for (const ch of token) {
        const charWidth = measure(ch);
        if (currentWidth > 0 && currentWidth + charWidth > maxWidth) {
          lines += 1;
          currentWidth = charWidth;
        } else {
          currentWidth += charWidth;
        }
      }
      return lines;
    };

    const spaceWidth = measure(' ');
    let totalLines = 0;

    content.split(/\n/).forEach((paragraph) => {
      const tokens = paragraph.trim().length > 0 ? paragraph.trim().split(/\s+/) : [];
      if (tokens.length === 0) {
        totalLines += 1;
        return;
      }

      let lineWidth = 0;
      let lineCount = 1;

      tokens.forEach((token) => {
        const tokenWidth = measure(token);
        if (tokenWidth > maxWidth) {
          if (lineWidth > 0) {
            lineCount += 1;
            lineWidth = 0;
          }
          const tokenLines = breakLongToken(token);
          lineCount += tokenLines - 1;
          lineWidth = tokenLines > 1 ? measure(token[token.length - 1] || '') : tokenWidth;
          return;
        }

        const nextWidth = lineWidth === 0 ? tokenWidth : lineWidth + spaceWidth + tokenWidth;
        if (nextWidth <= maxWidth) {
          lineWidth = nextWidth;
        } else {
          lineCount += 1;
          lineWidth = tokenWidth;
        }
      });

      totalLines += lineCount;
    });

    return Math.max(1, totalLines);
  };

  const estimateDesktopTagRows = (tagsList) => {
    if (!Array.isArray(tagsList) || tagsList.length === 0) return 0;
    const ctx = getTextMeasureContext();
    if (!ctx) return Math.min(5, Math.ceil(tagsList.length / 2));

    const desktopScale = getDesktopScaleFactor();
    ctx.font = `700 ${DESKTOP_FEED_CARD_METRICS.tagFontSize * desktopScale}px "TT Norms Pro", sans-serif`;
    const measure = (value) => {
      if (!value) return 0;
      return ctx.measureText(value).width + ((DESKTOP_FEED_CARD_METRICS.tagLetterSpacing * desktopScale) * Math.max(0, value.length - 1));
    };

    const maxWidth = DESKTOP_FEED_CARD_METRICS.tagAvailableWidth * desktopScale;
    const pillBaseWidth =
      ((DESKTOP_FEED_CARD_METRICS.tagPaddingX * 2) * desktopScale) +
      ((DESKTOP_FEED_CARD_METRICS.tagBorderWidth * 2) * desktopScale);
    let rows = 1;
    let currentRowWidth = 0;

    tagsList.forEach((tag) => {
      const pillWidth = Math.min(maxWidth, pillBaseWidth + measure(tag));
      const nextWidth = currentRowWidth === 0
        ? pillWidth
        : currentRowWidth + (DESKTOP_FEED_CARD_METRICS.tagColumnGap * desktopScale) + pillWidth;

      if (nextWidth <= maxWidth) {
        currentRowWidth = nextWidth;
      } else {
        rows += 1;
        currentRowWidth = pillWidth;
      }
    });

    return Math.max(1, rows);
  };

  const shiftDesktopSlotsNext = (fromSlots, nextCenterIndex, sourceItems, layoutMap) => ({
    farLeft: cloneSlotCard(fromSlots?.left),
    left: cloneSlotCard(fromSlots?.center),
    center: cloneSlotCard(fromSlots?.right),
    right: cloneSlotCard(fromSlots?.farRight),
    farRight: createFrozenSlotCard(sourceItems[nextCenterIndex + 2], layoutMap)
  });

  const shiftDesktopSlotsPrev = (fromSlots, prevCenterIndex, sourceItems, layoutMap) => ({
    farLeft: createFrozenSlotCard(sourceItems[prevCenterIndex - 2], layoutMap),
    left: cloneSlotCard(fromSlots?.farLeft),
    center: cloneSlotCard(fromSlots?.left),
    right: cloneSlotCard(fromSlots?.center),
    farRight: cloneSlotCard(fromSlots?.right)
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft') prev();
        if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prev, next]);

  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth < 768 || isAnimating) return;
    const currentItems = history.concat(feed);
    if (currentItems.length === 0) {
      setDesktopSlots(null);
      return;
    }

    const expectedCenterId = currentItems[index]?.id ?? null;
    const currentCenterId = desktopSlots?.center?.card?.id ?? null;

    // Freeze existing slots: rebuild only when the scene itself changed,
    // not when cardLayouts later receive refined measurements.
    if (!desktopSlots || currentCenterId !== expectedCenterId) {
      setDesktopSlots(buildDesktopSlots(index, currentItems, cardLayouts));
    }
  }, [feed, history, index, isAnimating]);

  useLayoutEffect(() => {
    const currentItems = history.concat(feed);
    const isDesktopViewport = typeof window !== 'undefined' && window.innerWidth >= 768;
    if (isDesktopViewport) return undefined;
    const transitionTargetIndex = cardTransition
      ? Math.max(0, Math.min(cardTransition.toIndex, currentItems.length - 1))
      : null;
    const layoutIndex = isDesktopViewport && transitionTargetIndex !== null ? transitionTargetIndex : index;
    const currentIdx = Math.max(0, Math.min(layoutIndex, currentItems.length - 1));
    const currentCard = currentItems[currentIdx];
    const currentPrevCard = currentIdx > 0 ? currentItems[currentIdx - 1] : null;
    const currentNextCard = currentIdx + 1 < currentItems.length ? currentItems[currentIdx + 1] : null;
    const cardsToMeasure = (
      isDesktopViewport
        ? [{ id: currentCard?.id, ref: mainCardRef }]
        : [
            { id: currentPrevCard?.id, ref: prevCardRef },
            { id: currentCard?.id, ref: mainCardRef },
            { id: currentNextCard?.id, ref: nextCardRef },
          ]
    ).filter((item) => item.id && item.ref.current);
    if (cardsToMeasure.length === 0) return undefined;

    let frame = 0;
    const px = (value) => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const outerHeight = (node) => {
      const styles = window.getComputedStyle(node);
      return node.offsetHeight + px(styles.marginTop) + px(styles.marginBottom);
    };
    const countTagRows = (tags) => {
      const children = Array.from(tags.children);
      if (children.length === 0) return { rows: 0, rowHeight: 0 };
      const previousMaxHeight = tags.style.maxHeight;
      const shell = tags.parentElement;
      const previousShellHeight = shell?.style.height;
      const previousShellOverflow = shell?.style.overflow;
      tags.style.maxHeight = 'none';
      if (shell) {
        shell.style.height = 'auto';
        shell.style.overflow = 'visible';
      }
      const tops = [];
      children.forEach((child) => {
        const top = Math.round(child.offsetTop);
        if (!tops.some((item) => Math.abs(item - top) <= 1)) tops.push(top);
      });
      const rowHeight = Math.max(...children.map((child) => child.offsetHeight));
      tags.style.maxHeight = previousMaxHeight;
      if (shell) {
        shell.style.height = previousShellHeight || '';
        shell.style.overflow = previousShellOverflow || '';
      }
      return { rows: tops.length, rowHeight };
    };
    const getFullBioLines = (bio, lineHeight) => {
      const previous = {
        display: bio.style.display,
        maxHeight: bio.style.maxHeight,
        overflow: bio.style.overflow,
        webkitLineClamp: bio.style.webkitLineClamp
      };
      bio.style.display = 'block';
      bio.style.maxHeight = 'none';
      bio.style.overflow = 'visible';
      bio.style.webkitLineClamp = 'unset';
      const lines = Math.max(1, Math.ceil(bio.scrollHeight / lineHeight));
      bio.style.display = previous.display;
      bio.style.maxHeight = previous.maxHeight;
      bio.style.overflow = previous.overflow;
      bio.style.webkitLineClamp = previous.webkitLineClamp;
      return lines;
    };
    const measureCardLayout = (root) => {
      if (!root || root.offsetHeight === 0 || window.getComputedStyle(root).display === 'none') {
        return null;
      }
      const body = root.querySelector('.feed-page__card-body-content');
      const tags = root.querySelector('.feed-page__card-tags');
      const bio = root.querySelector('.feed-page__card-bio');
      const actions = root.querySelector('.feed-page__card-actions');
      const title = body?.querySelector('h2');
      const meta = title?.nextElementSibling;
      if (!body || !tags || !bio || !actions || !title || !meta) return null;

      const tagStyles = window.getComputedStyle(tags);
      const bioStyles = window.getComputedStyle(bio);
      const { rows: possibleTagRows, rowHeight } = countTagRows(tags);
      const isMobile = window.innerWidth < 768;
      const fallbackRowGap = isMobile ? scaleMobileMetric(MOBILE_FEED_CARD_METRICS.tagRowGap) : scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.tagRowGap);
      const measuredRowGap = px(tagStyles.rowGap) || fallbackRowGap;
      const lineHeight = px(bioStyles.lineHeight) || (isMobile ? scaleMobileMetric(MOBILE_FEED_CARD_METRICS.bioLineHeight) : 1);
      const fullBioLines = getFullBioLines(bio, lineHeight);

      if (isMobile) {
        const minTagRows = possibleTagRows > 0 ? 2 : 0;
        const targetBioLines = Math.min(MAX_DESCRIPTION_LINES, fullBioLines);
        const tagTopSpace = scaleMobileMetric(MOBILE_FEED_CARD_METRICS.tagTopSpace);
        const tagBottomSpace = scaleMobileMetric(MOBILE_FEED_CARD_METRICS.tagBottomSpace);
        const mobileRowHeight = Math.max(getMobileTagRowHeight() * getMobileScaleFactor(), rowHeight);

        let nextTagRows = minTagRows;
        const fixedHeight = outerHeight(title) + outerHeight(meta) + outerHeight(actions) + (targetBioLines * lineHeight);
        const availableHeightForTags = Math.max(
          getTagBlockHeight(minTagRows, mobileRowHeight, measuredRowGap, tagTopSpace, tagBottomSpace),
          body.clientHeight - fixedHeight
        );

        const maxRowsByFormula = possibleTagRows > 0 ? MOBILE_FEED_CARD_METRICS.maxTagRows : 0;

        for (let rows = minTagRows + 1; rows <= maxRowsByFormula; rows += 1) {
          if (getTagBlockHeight(rows, mobileRowHeight, measuredRowGap, tagTopSpace, tagBottomSpace) <= availableHeightForTags) {
            nextTagRows = rows;
          } else {
            break;
          }
        }
        const visibleTagMetrics = getVisibleTagMetricsFromDom(tags, nextTagRows);

        return {
          '--feed-visible-tag-rows': nextTagRows,
          '--feed-visible-tag-count': visibleTagMetrics.visibleCount,
          '--feed-description-lines': targetBioLines,
          '--feed-tags-center-top': `${tagTopSpace}px`,
          '--feed-tags-center-bottom': `${tagBottomSpace}px`,
          '--feed-tags-block-height': `${visibleTagMetrics.blockHeight}px`,
          '--feed-description-truncated': fullBioLines > targetBioLines ? 1 : 0
        };
      }

      const minTagRows = possibleTagRows > 0 ? 2 : 0;
      const desktopRowHeight = Math.max(getDesktopTagRowHeight() * getDesktopScaleFactor(), rowHeight);
      const tagTopBase = scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.tagTopSpace);
      const tagBottomBase = scaleDesktopMetric(DESKTOP_FEED_CARD_METRICS.tagBottomSpace);
      const targetBioLines = Math.min(MAX_DESCRIPTION_LINES, fullBioLines);
      const bioHeight = targetBioLines * lineHeight;
      const fixedHeight = outerHeight(title) + outerHeight(meta) + outerHeight(actions) + bioHeight;
      const availableHeightForTags = Math.max(
        getDesktopTagBlockHeight(minTagRows, desktopRowHeight, measuredRowGap, tagTopBase, tagBottomBase),
        body.clientHeight - fixedHeight
      );
      let nextTagRows = minTagRows;

      for (let rows = minTagRows + 1; rows <= possibleTagRows; rows += 1) {
        if (getDesktopTagBlockHeight(rows, desktopRowHeight, measuredRowGap, tagTopBase, tagBottomBase) <= availableHeightForTags) {
          nextTagRows = rows;
        } else {
          break;
        }
      }
      const visibleTagMetrics = getVisibleTagMetricsFromDom(tags, nextTagRows);

      return {
        '--feed-visible-tag-rows': nextTagRows,
        '--feed-visible-tag-count': visibleTagMetrics.visibleCount,
        '--feed-description-lines': targetBioLines,
        '--feed-tags-center-top': `${tagTopBase}px`,
        '--feed-tags-center-bottom': `${tagBottomBase}px`,
        '--feed-tags-block-height': `${visibleTagMetrics.blockHeight}px`,
        '--feed-description-truncated': fullBioLines > targetBioLines ? 1 : 0
      };
    };
    const recalculate = () => {
      const nextLayouts = {};
      cardsToMeasure.forEach(({ id, ref }) => {
        const layout = measureCardLayout(ref.current);
        if (layout) nextLayouts[id] = layout;
      });
      if (Object.keys(nextLayouts).length === 0) return;
      setCardLayouts((prev) => {
        let changed = false;
        const merged = { ...prev };
        Object.entries(nextLayouts).forEach(([id, layout]) => {
          const old = prev[id];
          const same = old &&
            old['--feed-visible-tag-rows'] === layout['--feed-visible-tag-rows'] &&
            old['--feed-visible-tag-count'] === layout['--feed-visible-tag-count'] &&
            old['--feed-description-lines'] === layout['--feed-description-lines'] &&
            old['--feed-tags-center-top'] === layout['--feed-tags-center-top'] &&
            old['--feed-tags-center-bottom'] === layout['--feed-tags-center-bottom'] &&
            old['--feed-tags-block-height'] === layout['--feed-tags-block-height'];
          if (!same) {
            merged[id] = layout;
            changed = true;
          }
        });
        return changed ? merged : prev;
      });
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      setDesktopSlots(null);
      frame = window.requestAnimationFrame(recalculate);
    };

    recalculate();
    const observer = new ResizeObserver(schedule);
    cardsToMeasure.forEach(({ ref }) => observer.observe(ref.current));
    if (document.fonts?.ready) document.fonts.ready.then(schedule).catch(() => {});
    window.addEventListener('resize', schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [feed, history, index, cardTransition]);

  if (loading) return <div className="feed-page feed-root">Загрузка...</div>;

  const itemsToShow = history.concat(feed);
  const transitionFromIndex = cardTransition
    ? Math.max(0, Math.min(cardTransition.fromIndex, itemsToShow.length - 1))
    : null;
  const isDesktop = window.innerWidth >= 768;
  const transitionToIndex = cardTransition
    ? Math.max(0, Math.min(cardTransition.toIndex, itemsToShow.length - 1))
    : null;
  const desktopPhase = isDesktop ? cardTransition?.phase : null;
  const displayIndex = isDesktop
    ? (((cardTransition?.direction === 'next' && desktopPhase === 'stack-left') || (cardTransition?.direction === 'prev' && desktopPhase === 'stack-right'))
      ? transitionFromIndex
      : transitionToIndex ?? index)
    : (transitionFromIndex ?? index);
  const idx = Math.max(0, Math.min(displayIndex, itemsToShow.length - 1));
  const card = itemsToShow[idx];
  const transitionFromCard = transitionFromIndex !== null ? itemsToShow[transitionFromIndex] : null;
  const currentDesktopScene = isDesktop
    ? (
        isAnimating && desktopSlotTransition
          ? (((cardTransition?.direction === 'next' && desktopPhase === 'stack-left') || (cardTransition?.direction === 'prev' && desktopPhase === 'stack-right'))
              ? desktopSlotTransition.from
              : desktopSlotTransition.to)
          : (desktopSlots || buildDesktopSlots(index, itemsToShow, cardLayouts))
      )
    : null;
  
  const showEmptyScreen = !loading && itemsToShow.length === 0;

  // `itemsToShow` now always contains at least demo items, so `card` should exist.
  const nextCard = isDesktop ? currentDesktopScene?.right?.card ?? null : (idx + 1 < itemsToShow.length ? itemsToShow[idx + 1] : null);
  const prevCard = isDesktop ? currentDesktopScene?.left?.card ?? null : (idx > 0 ? itemsToShow[idx - 1] : null);
  const farPrevCard = isDesktop ? currentDesktopScene?.farLeft?.card ?? null : null;
  const farNextCard = isDesktop ? currentDesktopScene?.farRight?.card ?? null : null;
  const centerDesktopCard = isDesktop ? currentDesktopScene?.center?.card ?? null : null;
  const showDesktopStaticCenterCard = !(isDesktop && isAnimating);
  const hideDesktopPrevCard = false;
  const hideDesktopNextCard = false;
  const shouldFlattenLeftStackTop = isDesktop && Boolean(farPrevCard);
  const shouldFlattenRightStackTop = isDesktop && Boolean(farNextCard);
  const shouldFlattenMovingToLeftStack = isDesktop && Boolean(desktopSlotTransition?.from?.left?.card);
  const shouldFlattenMovingToRightStack = isDesktop && Boolean(desktopSlotTransition?.from?.right?.card);
  const hideDesktopCenterCard = isDesktop && isAnimating && (
    (cardTransition?.direction === 'next' && cardTransition?.phase === 'stack-left') ||
    (cardTransition?.direction === 'next' && cardTransition?.phase === 'promote-right') ||
    (cardTransition?.direction === 'prev' && cardTransition?.phase === 'stack-right') ||
    (cardTransition?.direction === 'prev' && cardTransition?.phase === 'promote-left')
  );

  const renderCard = (c, extraClasses = '', extraStyles = {}, ref = null, options = {}) => {
    if (!c) return null;
    const { onCardClick, keyOverride } = options;
    const isShadowless = extraClasses.includes('is-shadowless');
    const hasDesktopSideOverlay = extraClasses.includes('feed-page__desktop-side-visual');
    const hasExplicitDesktopLayout = window.innerWidth >= 768 && Object.keys(extraStyles || {}).length > 0;
    const resolvedLayout = {
      ...(window.innerWidth < 768
        ? getStaticCardLayout(c)
        : (hasExplicitDesktopLayout ? {} : (cardLayouts[c.id] || getStaticCardLayout(c)))),
      ...extraStyles
    };
    const visibleTagCount = Number(resolvedLayout['--feed-visible-tag-count'] ?? MAX_TAGS);
    return (
      <div
         key={keyOverride ?? c.id}
         ref={ref}
         onClick={onCardClick}
         className={`feed-page__main-card ${extraClasses.includes('absolute') ? '' : 'relative'} z-20 text-center flex flex-col items-center ${isShadowless ? 'shadow-none' : ''} ${extraClasses}`}

         style={resolvedLayout}




      >
         {/* Top Image Section - U-shape */}
         <div className="feed-page__card-photo-wrap relative flex justify-center">
              <div className="feed-page__card-photo overflow-hidden relative">
                  <img src={resolveAvatarThumbUrl(c.avatarUrl, 768, 'cover')} onError={withAvatarFallback()} style={{ objectPosition: '50% 22%' }} className="w-full h-full object-cover" alt="" />
              </div>
         </div>
         
         <div className="feed-page__card-body-content flex flex-col items-start w-full flex-1">



             <h2 className="feed-page__card-title">{getDisplayName(c)}</h2>
              <p className="feed-page__card-meta">
                 {c.city || 'Город'}{c.age ? `, ${c.age} лет` : ''}, {c.jobTitle || 'Профессия'}
             </p>
             
             {/* Centered tags area between meta and bottom block */}
             <div className="feed-page__card-tags-shell">
                 <div 
                   className="feed-page__card-tags flex flex-wrap w-full"
                   style={{ display: resolvedLayout['--feed-visible-tag-rows'] === 0 ? 'none' : 'flex' }}
                 >
                     {(getTags(c) || []).slice(0, visibleTagCount).map((t, i) => (
                         <span key={i}>{t}</span>
                     ))}
                 </div>
             </div>

             <div className="feed-page__card-bottom">
                 <div 
                   className="feed-page__card-bio-container cursor-pointer" 
                   onClick={(e) => { e.stopPropagation(); navigate(`/user/${c.id}`); }}
                   style={{ '--feed-description-truncated': resolvedLayout['--feed-description-truncated'] ?? 0 }}
                 >
                     <p className="feed-page__card-bio">
                         {c.bio || '...'}
                     </p>
                 </div>

                 {/* Buttons */}
                 <div className="feed-page__card-actions">





                 <button 
                     className="feed-page__card-action" 
                     onClick={(e) => { e.stopPropagation(); navigate(`/user/${c.id}`); }}
                     title="Профиль"
                 >
                     <svg className="feed-page__card-icon feed-page__card-icon--profile" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                         <path d="M20.5283 25.1221C28.8341 25.1221 33.6456 27.6065 36.3779 30.3389C39.1444 33.1056 39.8828 36.2253 39.8828 37.6348C39.8828 38.2846 39.7243 38.6943 39.5459 38.9619C39.3627 39.2368 39.1096 39.4349 38.8213 39.5791C38.5282 39.7256 38.2205 39.805 37.9766 39.8457C37.8574 39.8656 37.7606 39.8744 37.6982 39.8789C37.6673 39.8811 37.645 39.8824 37.6338 39.8828H3.42188C3.41061 39.8824 3.38829 39.8811 3.35742 39.8789C3.29512 39.8744 3.19871 39.8655 3.08008 39.8457C2.83616 39.805 2.52845 39.7256 2.23535 39.5791C1.94684 39.4348 1.69406 39.2369 1.51074 38.9619C1.33232 38.6943 1.17285 38.2848 1.17285 37.6348C1.17285 36.2253 1.91198 33.1056 4.67871 30.3389C7.4111 27.6065 12.2223 25.1221 20.5283 25.1221ZM20.5283 1.17285C22.9393 1.17292 25.2512 2.13114 26.9561 3.83594C28.6609 5.54075 29.619 7.85272 29.6191 10.2637C29.6191 12.6748 28.6609 14.9875 26.9561 16.6924C25.2512 18.3971 22.9392 19.3554 20.5283 19.3555C18.1172 19.3555 15.8045 18.3973 14.0996 16.6924C12.3947 14.9875 11.4375 12.6747 11.4375 10.2637C11.4376 7.8528 12.3949 5.54073 14.0996 3.83594C15.8045 2.13105 18.1172 1.17285 20.5283 1.17285Z" stroke="#FF6B35" strokeWidth="2.34606"/>
                     </svg>

                 </button>
                 
                 <button 
                     className={`feed-page__card-action feed-page__like-button ${
                       likeAnimations[c.id] ? `is-${likeAnimations[c.id]}` : ''
                     }`}
                     onClick={(e)=> { e.stopPropagation(); toggleLike(c.id); }}
                     onAnimationEnd={() => {
                       setLikeAnimations((prev) => {
                         if (!prev[c.id]) return prev;
                         const next = { ...prev };
                         delete next[c.id];
                         return next;
                       });
                     }}
                     title="Лайк"
                 >
                     <svg className="feed-page__card-icon feed-page__card-icon--like feed-page__like-icon" viewBox="0 0 46 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                         <path
                           className={`feed-page__like-fill ${likedSet.has(c.id) || c.isLikedByMe ? 'is-filled' : ''}`}
                           d="M22.0508 4.43262C16.3137 -1.21216 6.12312 0.875587 2.49316 7.66113C0.724758 10.9669 0.464632 15.5244 3.3877 21.0449C6.2729 26.4938 12.2556 32.8491 22.874 39.6631C33.4923 32.8498 39.4752 26.4946 42.3604 21.0459C45.2833 15.5256 45.0233 10.968 43.2549 7.66211C39.6248 0.87598 29.4333 -1.21245 23.6963 4.43262L22.874 5.24219L22.0508 4.43262Z"
                           fill="#FF6B35"
                         />
                         <path d="M22.0508 4.43262C16.3137 -1.21216 6.12312 0.875587 2.49316 7.66113C0.724758 10.9669 0.464632 15.5244 3.3877 21.0449C6.2729 26.4938 12.2556 32.8491 22.874 39.6631C33.4923 32.8498 39.4752 26.4946 42.3604 21.0459C45.2833 15.5256 45.0233 10.968 43.2549 7.66211C39.6248 0.87598 29.4333 -1.21245 23.6963 4.43262L22.874 5.24219L22.0508 4.43262Z" fill="none" stroke="#FF6B35" strokeWidth="2.34606"/>
                     </svg>
                 </button>

                 <button 
                     className="feed-page__card-action" 
                     onClick={(e) => { e.stopPropagation(); markNotInterested(c.id); }}
                     title="Не интересно"
                 >
                     
                         <svg className="feed-page__card-icon feed-page__card-icon--dislike" viewBox="0 0 51 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                             <path d="M25.5723 4.42773C19.9223 -1.20397 9.8805 0.87019 6.29688 7.65625C4.5494 10.9657 4.29306 15.5283 7.17969 21.0508C10.0269 26.4977 15.929 32.848 26.4004 39.6572C36.8718 32.8487 42.7739 26.4985 45.6211 21.0518C48.5077 15.5295 48.2514 10.9668 46.5039 7.65723C42.9202 0.870738 32.8784 -1.20389 27.2285 4.42773L26.4004 5.25293L25.5723 4.42773Z" stroke="#FF6B35" strokeWidth="2.34606"/>
                             <path fillRule="evenodd" clipRule="evenodd" d="M50.7475 2.97149L2.01876 39.4707L0 36.4997L48.7287 0.000457764L50.7475 2.97149Z" fill="#FF6B35"/>
                         </svg>
                 </button>
                 </div>
             </div>
         </div>
         {hasDesktopSideOverlay ? <div className="feed-page__side-overlay" aria-hidden="true" /> : null}
      </div>
    );
  };




                 




  return (
    <div className="feed-page min-h-screen flex flex-col font-tt-norms overflow-hidden">
      <style>{`
        @keyframes slideInFromLeft {
          from { transform: translateX(-100vw); }
          to { transform: translateX(0); }
        }
        @keyframes slideOutToLeft {
          from { transform: translateX(0); }
          to { transform: translateX(-100vw); }
        }
        .animate-slide-in-left {
          animation: slideInFromLeft 0.65s ease-in-out forwards;
        }
        .animate-slide-out-left {
          animation: slideOutToLeft 0.65s ease-in-out forwards;
        }
      `}</style>
      <main className="feed-page__main flex-grow mx-auto">
        {/* Back Button */}
        <div className="feed-page__back">
          <button onClick={() => navigate(-1)} className="feed-page__back-button">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Назад
          </button>
        </div>

        <div className="feed-page__shell w-full relative mx-auto">
        {finished || showEmptyScreen ? (
          <div className="feed-page__empty w-full flex flex-col h-full relative">
             
             <div className="feed-page__empty-content flex flex-col items-center justify-center flex-grow text-center">
                 <h2 className="feed-page__empty-title">К сожалению, сейчас мы не нашли подходящих анкет</h2>
                 <p className="feed-page__empty-text">Загляни сюда чуть позже — анкеты обновляются каждый день</p>
             </div>
          </div>
        ) : (
          <div 
            className="feed-page__carousel relative w-full flex items-center justify-center touch-pan-y"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
             {window.innerWidth >= 768 && farPrevCard && renderCard(
               farPrevCard,
               'feed-page__adjacent-card feed-page__adjacent-card--far-left feed-page__desktop-side-visual hidden md:flex absolute left-1/2 top-1/2 z-0',
               currentDesktopScene?.farLeft?.layout || {},
               null,
               { keyOverride: `far-prev-${farPrevCard.id}` }
             )}

             {window.innerWidth >= 768 && farNextCard && renderCard(
               farNextCard,
               'feed-page__adjacent-card feed-page__adjacent-card--far-right feed-page__desktop-side-visual hidden md:flex absolute left-1/2 top-1/2 z-0',
               currentDesktopScene?.farRight?.layout || {},
               null,
               { keyOverride: `far-next-${farNextCard.id}` }
             )}

             {window.innerWidth >= 768 && prevCard && !hideDesktopPrevCard && renderCard(
               prevCard,
               `feed-page__adjacent-card feed-page__adjacent-card--left feed-page__desktop-shadow-fade ${shouldFlattenLeftStackTop ? 'feed-page__desktop-side-top is-shadowless' : ''} feed-page__desktop-side-visual hidden md:flex absolute left-1/2 top-1/2 z-10`,
               currentDesktopScene?.left?.layout || {},
               prevCardRef,
               { onCardClick: prev, keyOverride: `prev-${prevCard.id}` }
             )}

             {window.innerWidth >= 768 && nextCard && !hideDesktopNextCard && renderCard(
               nextCard,
               `feed-page__adjacent-card feed-page__adjacent-card--right feed-page__desktop-shadow-fade ${shouldFlattenRightStackTop ? 'feed-page__desktop-side-top is-shadowless' : ''} feed-page__desktop-side-visual hidden md:flex absolute left-1/2 top-1/2 z-10`,
               currentDesktopScene?.right?.layout || {},
               nextCardRef,
               { onCardClick: next, keyOverride: `next-${nextCard.id}` }
             )}

             {window.innerWidth >= 768 && isAnimating && cardTransition?.direction === 'next' && cardTransition?.phase === 'stack-left' && desktopSlotTransition?.from?.center?.card && (
               renderCard(
                 desktopSlotTransition.from.center.card,
                 `absolute left-1/2 top-1/2 z-40 feed-page__desktop-overlay feed-page__desktop-overlay--side-safe ${shouldFlattenMovingToLeftStack ? 'feed-page__desktop-overlay--to-left-stack feed-page__desktop-side-top is-shadowless' : 'feed-page__desktop-overlay--to-left-stack-solo'}`,
                 desktopSlotTransition.from.center.layout || {},
                 null,
                 { keyOverride: `desktop-next-stack-left-${desktopSlotTransition.from.center.card.id}-${cardTransition.runId}` }
               )
             )}

             {window.innerWidth >= 768 && isAnimating && cardTransition?.direction === 'next' && cardTransition?.phase === 'promote-right' && desktopSlotTransition?.to?.center?.card && (
               renderCard(
                 desktopSlotTransition.to.center.card,
                 'absolute left-1/2 top-1/2 z-40 feed-page__desktop-overlay feed-page__desktop-overlay--from-right-to-center',
                 desktopSlotTransition.to.center.layout || {},
                 null,
                 { keyOverride: `desktop-next-promote-right-${desktopSlotTransition.to.center.card.id}-${cardTransition.runId}` }
               )
             )}

             {window.innerWidth >= 768 && isAnimating && cardTransition?.direction === 'prev' && cardTransition?.phase === 'stack-right' && desktopSlotTransition?.from?.center?.card && (
               renderCard(
                 desktopSlotTransition.from.center.card,
                 `absolute left-1/2 top-1/2 z-40 feed-page__desktop-overlay feed-page__desktop-overlay--side-safe ${shouldFlattenMovingToRightStack ? 'feed-page__desktop-overlay--to-right-stack feed-page__desktop-side-top is-shadowless' : 'feed-page__desktop-overlay--to-right-stack-solo'}`,
                 desktopSlotTransition.from.center.layout || {},
                 null,
                 { keyOverride: `desktop-prev-stack-right-${desktopSlotTransition.from.center.card.id}-${cardTransition.runId}` }
               )
             )}

             {window.innerWidth >= 768 && isAnimating && cardTransition?.direction === 'prev' && cardTransition?.phase === 'promote-left' && desktopSlotTransition?.to?.center?.card && (
               renderCard(
                 desktopSlotTransition.to.center.card,
                 'absolute left-1/2 top-1/2 z-40 feed-page__desktop-overlay feed-page__desktop-overlay--from-left-to-center',
                 desktopSlotTransition.to.center.layout || {},
                 null,
                 { keyOverride: `desktop-prev-promote-left-${desktopSlotTransition.to.center.card.id}-${cardTransition.runId}` }
               )
             )}

             {window.innerWidth < 768 && isAnimating && cardTransition?.direction === 'next' && nextCard && (
               renderCard(
                 nextCard,
                 'absolute left-0 right-0 mx-auto z-20',
                 {},
                 null,
                 { keyOverride: `mobile-next-background-${nextCard.id}-${cardTransition.runId}` }
               )
             )}

             {/* Main Center Card */}
             {(isDesktop ? centerDesktopCard : card) && showDesktopStaticCenterCard && renderCard(
               isDesktop ? centerDesktopCard : card,
               `${window.innerWidth >= 768 ? 'feed-page__center-card absolute left-1/2 top-1/2 z-20' : ''} ${hideDesktopCenterCard ? 'feed-page__desktop-center-hidden' : ''} ${(window.innerWidth < 768 && isAnimating && cardTransition?.direction === 'next') ? 'absolute left-0 right-0 mx-auto z-30 animate-slide-out-left is-shadowless' : ''}`,
               isDesktop ? (currentDesktopScene?.center?.layout || {}) : {},
               mainCardRef,
               {
                 keyOverride: `${window.innerWidth >= 768 ? 'desktop-center' : 'mobile-center'}-${(isDesktop ? centerDesktopCard : card).id}-${index}`
               }
             )}

             {window.innerWidth < 768 && isAnimating && cardTransition?.direction === 'prev' && prevCard && (
               renderCard(
                 prevCard,
                 'absolute left-0 right-0 mx-auto z-30 animate-slide-in-left is-shadowless',
                 {},
                 null,
                 { keyOverride: `mobile-prev-overlay-${prevCard.id}-${cardTransition.runId}` }
               )
             )}



             {/* Navigation Arrows - Hidden on mobile, shown on desktop */}

             {itemsToShow.length > 1 && index > 0 && (
                <button 
                    onClick={prev} 
                    disabled={isAnimating}
                    className="feed-page__arrow feed-page__arrow--prev hidden md:flex absolute top-1/2 -translate-y-1/2 items-center justify-center z-50"
                >
                    <svg viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.5205 6.76801e-05C16.5477 6.74191e-05 10.8196 2.37275 6.59616 6.59616C2.37275 10.8196 6.991e-05 16.5477 6.9649e-05 22.5205C6.93879e-05 28.4933 2.37275 34.2215 6.59616 38.4449C10.8196 42.6683 16.5477 45.041 22.5205 45.041C28.4933 45.041 34.2215 42.6683 38.4449 38.4449C42.6683 34.2215 45.041 28.4933 45.041 22.5205C45.041 16.5477 42.6683 10.8196 38.4449 6.59616C34.2215 2.37275 28.4933 6.79412e-05 22.5205 6.76801e-05ZM32.3733 21.113C32.7466 21.113 33.1046 21.2613 33.3685 21.5253C33.6325 21.7892 33.7808 22.1472 33.7808 22.5205C33.7808 22.8938 33.6325 23.2519 33.3685 23.5158C33.1046 23.7798 32.7466 23.9281 32.3732 23.9281L16.0656 23.9281L22.1095 29.9692C22.3738 30.2335 22.5223 30.5919 22.5223 30.9657C22.5223 31.3395 22.3738 31.698 22.1095 31.9622C21.8452 32.2265 21.4868 32.375 21.113 32.375C20.7392 32.375 20.3808 32.2265 20.1165 31.9622L11.6713 23.5171C11.5402 23.3863 11.4362 23.231 11.3653 23.06C11.2943 22.889 11.2578 22.7057 11.2578 22.5205C11.2578 22.3354 11.2943 22.1521 11.3653 21.9811C11.4362 21.8101 11.5402 21.6548 11.6713 21.524L20.1165 13.0788C20.3808 12.8145 20.7392 12.6661 21.113 12.6661C21.4868 12.6661 21.8452 12.8145 22.1095 13.0788C22.3738 13.3431 22.5223 13.7016 22.5223 14.0754C22.5223 14.4491 22.3738 14.8076 22.1095 15.0719L16.0656 21.113L32.3733 21.113Z" fill="currentColor"/>
                    </svg>
                </button>
             )}
             
             <button 
                onClick={next} 
                disabled={isAnimating}
                className="feed-page__arrow feed-page__arrow--next hidden md:flex absolute top-1/2 -translate-y-1/2 items-center justify-center z-50"
             >
                <svg viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.5205 6.76801e-05C28.4933 6.74191e-05 34.2214 2.37275 38.4449 6.59616C42.6683 10.8196 45.0409 16.5477 45.0409 22.5205C45.0409 28.4933 42.6683 34.2215 38.4449 38.4449C34.2214 42.6683 28.4933 45.041 22.5205 45.041C16.5477 45.041 10.8195 42.6683 6.5961 38.4449C2.37269 34.2215 1.24548e-06 28.4933 9.84402e-07 22.5205C7.23323e-07 16.5477 2.37269 10.8196 6.59609 6.59616C10.8195 2.37275 16.5477 6.79412e-05 22.5205 6.76801e-05ZM12.6678 21.113C12.2945 21.113 11.9365 21.2613 11.6725 21.5253C11.4085 21.7892 11.2602 22.1472 11.2602 22.5205C11.2602 22.8938 11.4085 23.2519 11.6725 23.5158C11.9365 23.7798 12.2945 23.9281 12.6678 23.9281L28.9754 23.9281L22.9315 29.9692C22.6672 30.2335 22.5187 30.5919 22.5187 30.9657C22.5187 31.3395 22.6672 31.698 22.9315 31.9622C23.1958 32.2265 23.5542 32.375 23.928 32.375C24.3018 32.375 24.6602 32.2265 24.9245 31.9622L33.3697 23.5171C33.5008 23.3863 33.6048 23.231 33.6757 23.06C33.7467 22.889 33.7832 22.7057 33.7832 22.5205C33.7832 22.3354 33.7467 22.1521 33.6757 21.9811C33.6048 21.8101 33.5008 21.6548 33.3697 21.524L24.9245 13.0788C24.6602 12.8145 24.3018 12.6661 23.928 12.6661C23.5542 12.6661 23.1958 12.8145 22.9315 13.0788C22.6672 13.3431 22.5187 13.7016 22.5187 14.0754C22.5187 14.4491 22.6672 14.8076 22.9315 15.0719L28.9754 21.113L12.6678 21.113Z" fill="currentColor"/>
                </svg>
             </button>
          </div>
        )}
        </div>
      </main>
      <Footer/>
    </div>
  );
}

